import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { ProjectSubmodule } from '../project-submodule.interface';
import { AuthService } from '../../../auth/auth.service';
import { ElementService } from '../../element/element.service';
import { SubmoduleOptionsActions } from './submodule-options-action.interface';
import { SubmoduleService } from '../submodule.service';
import { ScrollToService } from '../../../utilities/scroll-to/scroll-to.service';
import { ToastService } from '../../../services/toast/toast.service';
import { TranslateService } from '../../../utilities/translate/translate.service';
import { ConstantsService } from '../../../utilities/constants/constants.service';
import { ConstantsStatusType } from '../../../utilities/constants/constants.interface';
import { UserService } from '../../user/user.service';
import { User } from '../../user/user.interface';
import { ModalService } from '../../../services/modal/modal.service';
import { ParagraphAddAction } from '../../../services/modal/modals/paragraph-add/paragraph-add.interface';

@Component({
  selector: 'sg2-submodule-options',
  templateUrl: './submodule-options.component.html',
  styleUrls: ['./submodule-options.component.less']
})
export class SubmoduleOptionsComponent implements OnInit {

  /** Default options. */
  private readonly OPTIONS_DEFAULT: SubmoduleOptionsActions['action'][] = ['delete'];

  /** Disabled state of menu. */
  @Input() disabled: boolean;

  /** Submodule. */
  @Input() submodule: ProjectSubmodule;

  /** Set custom selection of options. */
  @Input() options: string[];

  /** Dispatch action. Kind of @depricated with new datastore. */
  @Output() action = new EventEmitter<SubmoduleOptionsActions>();

  /** Constants status list. */
  public statuses: ConstantsStatusType[];

  /** Configuration. */
  public config: {
    addElement?: boolean;
    assignTo?: boolean;
    delete?: boolean;
    setStatus?: boolean;
  } = { };


  constructor(
    public authService: AuthService,
    private constantsService: ConstantsService,
    private elementService: ElementService,
    private modalService: ModalService,
    private scrollToService: ScrollToService,
    private submoduleService: SubmoduleService,
    private toastService: ToastService,
    private translateService: TranslateService,
    public userService: UserService
  ) { }

  ngOnInit(): void {
    this.statuses = this.constantsService.getStatusTypes();
    this._setOptions();
  }

  /**
   * Add element to the end of the submodule.
   *
   * Support for:
   * - Empty paragraph. The default template for empty paragraphs is used.
   * - Copy from template library. The template is used to create a new element.
   */
  public addElement(): void {
    this.modalService.openParagraphAdd(this.submodule).afterClosed().subscribe((results: ParagraphAddAction) => {
      if (results && results.action === 'new') { // New empty element.
        this.elementService.addParagraph(this.submodule).subscribe(element => {
          this.toastService.open(this.translateService.get('specificationadded'));
          this.scrollToService.find(element._id, { animate: 'flash' });
        });
      } else if (results && results.action === 'template') { // From template.
        this.elementService.add(results.element).subscribe(element => {
          this.toastService.open(this.translateService.get('specificationadded'));
          this.scrollToService.find(element._id, { animate: 'flash' });
        });
      }
    });
  }

  /**
   * Assign all elements to user.
   *
   * @param submodule The parent submodule of the elements.
   * @param user The user to assign to each element.
   */
  public assignToUser(submodule: ProjectSubmodule, user: User): void {
    this.elementService.assignToBulk(submodule.elements.filter(e => e.active), user, true);
  }

  /**
   * Delete submodule.
   *
   * @param submodule The submodule to delete.
   */
  public delete(submodule: ProjectSubmodule): void {
    this.submoduleService.delete(submodule, true);
  }

  /**
   * Set status of each element.
   *
   * @param submodule The parent submodule of the elements.
   * @param status The status to set on each element.
   */
  public setStatus(submodule: ProjectSubmodule, status: ConstantsStatusType) {
    this.elementService.setStatusBulk(submodule.elements.filter(e => e.active), status._id, true);
  }

  /**
   * Set menu options.
   *
   * A custom selection of options can be added by using the options binding.
   * When no options are given, the default selection is used.
   */
  private _setOptions(): void {
    this.options = this.options || this.OPTIONS_DEFAULT;
    this.options.forEach(o => {
      this.config[o] = true;
    });
  }
}
